// src/components/atomic-crm/companies/CompanyCard.tsx
import { Link } from "react-router";
import { useCreatePath, useListContext, useRecordContext } from "ra-core";
import { ReferenceManyField } from "@/components/ds/admin/reference-many-field";
import { Card } from "@/components/ds/ui/card";
import { cn } from "@/lib/utils";

import { Avatar as ContactAvatar } from "../contacts/Avatar";
import type { Company } from "../types";
import { CompanyAvatar } from "./CompanyAvatar";

export const CompanyCard = (props: { record?: Company }) => {
  const createPath = useCreatePath();
  const record = useRecordContext<Company>(props);
  if (!record) return null;

  return (
    <Link
      to={createPath({
        resource: "companies",
        id: record.id,
        type: "show",
      })}
      className="no-underline block group"
    >
      <Card
        className={cn(
          // Base shape
          "h-[200px] flex flex-col justify-between p-4",
          // Border + surface
          "border border-border/60 bg-card",
          // Elevation at rest
          "shadow-sm",
          // Smooth everything
          "transition-all duration-200 ease-out",
          // Hover state — lift and brighten
          "group-hover:shadow-md group-hover:-translate-y-0.5",
          "group-hover:border-border group-hover:bg-card",
          // Focus-visible for keyboard users
          "group-focus-visible:ring-2 group-focus-visible:ring-ring group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-background",
        )}
      >
        <div className="flex flex-col items-center gap-1">
          {/* Ring around the avatar for definition */}
          <div className="rounded-full ring-1 ring-border/60 ring-offset-2 ring-offset-card">
            <CompanyAvatar />
          </div>
          <div className="text-center mt-1">
            <h6 className="text-sm font-medium truncate max-w-full">
              {record.name}
            </h6>
            <p className="text-xs text-muted-foreground truncate max-w-full">
              {record.sector}
            </p>
          </div>
        </div>

        <div className="flex flex-row w-full justify-between gap-2 items-center">
          <div className="flex items-center min-h-[25px]">
            {record.nb_contacts ? (
              <ReferenceManyField reference="contacts" target="company_id">
                <AvatarGroupIterator />
              </ReferenceManyField>
            ) : null}
          </div>
          {record.nb_deals ? (
            <div className="flex items-center gap-1">
              <span className="text-sm font-semibold tabular-nums">
                {record.nb_deals}
              </span>
              <span className="text-xs text-muted-foreground">
                {record.nb_deals > 1 ? "deals" : "deal"}
              </span>
            </div>
          ) : null}
        </div>
      </Card>
    </Link>
  );
};

const AvatarGroupIterator = () => {
  const { data, total, error, isPending } = useListContext();
  if (isPending || error) return null;

  const MAX_AVATARS = 3;
  return (
    <div className="flex -space-x-1.5">
      {data.slice(0, MAX_AVATARS).map((record: any) => (
        <div
          key={record.id}
          className="rounded-full ring-2 ring-card"
          title={`${record.first_name} ${record.last_name}`}
        >
          <ContactAvatar record={record} width={25} height={25} />
        </div>
      ))}
      {total > MAX_AVATARS && (
        <div className="flex w-[25px] h-[25px] items-center justify-center rounded-full bg-muted text-[10px] font-medium ring-2 ring-card">
          +{total - MAX_AVATARS}
        </div>
      )}
    </div>
  );
};
